import i18n from "~/i18n/i18n";
import { formatEaches, formatUnits, UNIT_DECIMALS } from "~fulfill/orders/helpers/orderLineUnits";
import shippingUomLabels from "~fulfill/product/helpers/shippingUomLabels";

const tf = (key: string, options?: Record<string, unknown>): string => i18n.t(key, { ns: "fulfill", ...options });

const BASIS_NOUN = "each";
const BASIS_NOUN_SHORT = "ea";

const priceBasisSuffix = (basis: string): string => `/ ${basis}`;
const priceWithBasis = (formattedPrice: string, basis: string): string =>
	`${formattedPrice} ${priceBasisSuffix(basis)}`;
const pricePerEachShort = (formattedPrice: string): string => priceWithBasis(formattedPrice, BASIS_NOUN_SHORT);

const hasMultiEachPack = (eachesPerUnit: null | number | undefined): boolean => (eachesPerUnit ?? 0) > 1;

const quantityLabel = (packLabel: null | string | undefined, eachesPerUnit: null | number | undefined): string =>
	hasMultiEachPack(eachesPerUnit) && packLabel
		? tf("orders.pricingBasis.quantityWithPack", { pack: packLabel })
		: tf("orders.pricingBasis.quantity");

const entryQuantityLabel = (
	eachesEntry: boolean,
	packLabel: null | string | undefined,
	eachesPerUnit: null | number | undefined
): string => (eachesEntry ? tf("orders.pricingBasis.quantityEaches") : quantityLabel(packLabel, eachesPerUnit));

const bothQuantities = (eaches: number, units: number, packLabel: null | string | undefined): string =>
	shippingUomLabels.unitCountWithEaches(
		Number(units.toFixed(UNIT_DECIMALS)),
		eaches,
		packLabel || tf("orders.pricingBasis.unitsFallback")
	);

const packSizeNote = (packLabel: null | string | undefined, eachesPerUnit: null | number | undefined): string =>
	hasMultiEachPack(eachesPerUnit) && packLabel
		? tf("orders.pricingBasis.packSizeNote", { eaches: eachesPerUnit, pack: packLabel })
		: "";

const extensionExplanation = (
	orderedUnits: number,
	packLabel: null | string | undefined,
	eachesPerUnit: number,
	formattedUnitPrice: string
): string =>
	packLabel
		? tf("orders.pricingBasis.extensionWithPack", {
				eachesPerUnit: formatEaches(eachesPerUnit),
				pack: packLabel,
				price: formattedUnitPrice,
				units: formatUnits(orderedUnits)
			})
		: tf("orders.pricingBasis.extension", {
				eachesPerUnit: formatEaches(eachesPerUnit),
				price: formattedUnitPrice,
				units: formatUnits(orderedUnits)
			});

const eachesExtensionExplanation = (
	orderedEaches: number,
	formattedUnitPrice: string,
	formattedLineTotal: string
): string =>
	tf("orders.pricingBasis.eachesExtension", {
		count: orderedEaches,
		eaches: formatEaches(orderedEaches),
		price: formattedUnitPrice,
		total: formattedLineTotal
	});

const fallbackPackLabel = (packSize: null | number | undefined): string => {
	if (packSize == null || packSize <= 0) return "";
	return packSize === 1
		? tf("orders.pricingBasis.packFallbackEach")
		: tf("orders.pricingBasis.packFallbackPack", { size: packSize });
};

const priceTooltipFor = (eachesEntry: boolean): string =>
	eachesEntry ? tf("orders.pricingBasis.eachesEntryPriceTooltip") : tf("orders.pricingBasis.priceTooltip");

const priceLabelFor = (unitEntry: boolean): string =>
	unitEntry ? tf("orders.pricingBasis.unitPrice") : tf("orders.pricingBasis.pricePerEach");

const overridePriceLabelFor = (unitEntry: boolean): string =>
	unitEntry ? tf("orders.pricingBasis.overrideUnitPrice") : tf("orders.pricingBasis.overridePricePerEach");

const unitPriceTooltipFor = (formattedPerEach: string): string =>
	tf("orders.pricingBasis.unitPriceTooltip", { perEach: formattedPerEach });

const allocatedEachesHeaderFor = (hasUnitPartner: boolean): string =>
	hasUnitPartner ? tf("orders.pricingBasis.allocatedEaches") : tf("orders.pricingBasis.allocatedQuantity");

const pricingBasisLabels = {
	get acknowledgedEachesLabel(): string {
		return tf("orders.pricingBasis.acknowledgedEaches");
	},
	allocatedEachesHeaderFor,
	bothQuantities,
	entryQuantityLabel,
	get orderedEachesEnteredTooltip(): string {
		return tf("orders.pricingBasis.orderedEachesEnteredTooltip");
	},
	get orderedUnitsRoundedUpTooltip(): string {
		return tf("orders.pricingBasis.orderedUnitsRoundedUpTooltip");
	},
	get acknowledgedUnitsLabel(): string {
		return tf("orders.pricingBasis.acknowledgedUnits");
	},
	get allocatedEachesLabel(): string {
		return tf("orders.pricingBasis.allocatedEaches");
	},
	get allocatedUnitsLabel(): string {
		return tf("orders.pricingBasis.allocatedUnits");
	},
	get availableStockLabel(): string {
		return tf("orders.pricingBasis.availableStock");
	},
	get availableStockTooltip(): string {
		return tf("orders.pricingBasis.availableStockTooltip");
	},
	basisNoun: BASIS_NOUN,
	basisNounShort: BASIS_NOUN_SHORT,
	eachesExtensionExplanation,
	extensionExplanation,
	fallbackPackLabel,
	hasMultiEachPack,
	get linePackSizeTooltip(): string {
		return tf("orders.pricingBasis.linePackSizeTooltip", { total: tf("orders.pricingBasis.totalEaches") });
	},
	get orderedEachesLabel(): string {
		return tf("orders.pricingBasis.orderedEaches");
	},
	get orderedEachesTooltip(): string {
		return tf("orders.pricingBasis.orderedEachesTooltip");
	},
	get orderedUnitsLabel(): string {
		return tf("orders.pricingBasis.orderedUnits");
	},
	get orderedUnitsTooltip(): string {
		return tf("orders.pricingBasis.orderedUnitsTooltip");
	},
	overridePriceLabelFor,
	get overridePriceLabel(): string {
		return tf("orders.pricingBasis.overridePricePerEach");
	},
	get pricePerUnitLabel(): string {
		return tf("orders.pricingBasis.pricePerUnit");
	},
	get pricePerUnitTooltip(): string {
		return tf("orders.pricingBasis.pricePerUnitTooltip");
	},
	packSizeNote,
	get packSizeTooltip(): string {
		return tf("orders.pricingBasis.packSizeTooltip", { total: tf("orders.pricingBasis.totalEaches") });
	},
	priceBasisSuffix,
	get priceLabel(): string {
		return tf("orders.pricingBasis.pricePerEach");
	},
	pricePerEachShort,
	priceLabelFor,
	get priceTooltip(): string {
		return tf("orders.pricingBasis.priceTooltip");
	},
	priceTooltipFor,
	priceWithBasis,
	quantityLabel,
	unitPriceTooltipFor,
	get shippedEachesLabel(): string {
		return tf("orders.pricingBasis.shippedEaches");
	},
	get shippedUnitsLabel(): string {
		return tf("orders.pricingBasis.shippedUnits");
	},
	get totalBasisLabel(): string {
		return tf("orders.pricingBasis.totalEaches");
	},
	get unitsComputedTooltip(): string {
		return tf("orders.pricingBasis.unitsComputedTooltip");
	}
} as const;

export default pricingBasisLabels;
